import {getMarket,formatMarketMoney} from "../utils/market";
import React from "react";
import { getBusinessLogo } from "./SmartLogoFrame";

const BRAND = "#00675f";
const FALLBACK_IMAGE = (process.env.PUBLIC_URL || "") + "/no-image.png";

function text(value, fallback = "") {
  const clean = String(value ?? "").trim();
  return clean || fallback;
}

function initials(name) {
  const clean = text(name, "Loop");
  const parts = clean.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0]?.[0] || ""}${parts[1]?.[0] || ""}`.toUpperCase();
}

function normalizeServiceName(service) {
  if (!service) return "";
  if (typeof service === "string") return service;
  return service.name || service.title || service.label || service.serviceName || service.id || "";
}

export function getBusinessName(biz) {
  return text(biz?.name || biz?.businessName || biz?.title, "Negocio");
}

export function getBusinessImage(biz) {
  const candidates = [
    biz?.coverImageUrl,
    biz?.coverImage,
    biz?.mainPhotoUrl,
    biz?.menuImage,
    biz?.logoUrl,
    biz?.logo,
    biz?.logo_url,
    biz?.logoImage,
    biz?.logo_img,
    biz?.logoPath,
    biz?.coverImage,
    biz?.mainPhotoUrl,
    biz?.photoUrl,
    biz?.image,
    biz?.img,
    biz?.hero,
    biz?.templatePhoto,
    biz?.templateImage,
    biz?.imageUrl,
    biz?.coverImageUrl,
    getBusinessLogo(biz),
  ];

  for (const candidate of candidates) {
    if (!candidate) continue;
    if (typeof candidate === "string" && candidate.trim()) return candidate.trim();
    if (candidate && typeof candidate === "object" && candidate.url) return candidate.url;
  }

  return FALLBACK_IMAGE;
}

const PANAMA_ZONE_HINTS = [
  "Costa del Este",
  "San Francisco",
  "Punta Pacífica",
  "Punta Pacifica",
  "Obarrio",
  "El Cangrejo",
  "Bella Vista",
  "Marbella",
  "Betania",
  "El Dorado",
  "Condado del Rey",
  "Brisas del Golf",
  "Costa Sur",
  "Santa María",
  "Santa Maria",
  "Coco del Mar",
  "Parque Lefevre",
  "Parque Lefèvre",
  "Vía España",
  "Via España",
  "Albrook",
  "Clayton",
  "Casco Viejo",
  "Arraiján",
  "Arraijan",
  "Panamá Pacífico",
  "Panama Pacifico",
  "La Chorrera",
];

function normalizeLocationKey(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function extractZoneFromLocation(value) {
  const raw = text(value, "");
  if (!raw) return "";

  const normalized = normalizeLocationKey(raw);
  for (const zone of PANAMA_ZONE_HINTS) {
    const z = normalizeLocationKey(zone);
    if (!z) continue;
    if (normalized === z || normalized.startsWith(`${z} `) || normalized.includes(` ${z} `)) {
      return zone;
    }
  }

  const firstChunk = raw
    .split(",")
    .map((part) => part.trim())
    .find(Boolean);

  if (!firstChunk) return "";

  const beforeStreet = firstChunk
    .split(/\b(?:calle|av\.?|avenida|v[ií]a|plaza|mall|edificio|grupo|torre|local)\b/i)[0]
    .trim();

  if (beforeStreet && beforeStreet.length >= 3 && beforeStreet.length <= 28) return beforeStreet;
  return firstChunk.length <= 28 ? firstChunk : "";
}

export function getBusinessLocation(biz) {
  const directZone = text(
    biz?.district ||
      biz?.sector ||
      biz?.neighborhood ||
      biz?.neighbourhood ||
      biz?.corregimiento ||
      biz?.zone ||
      biz?.area ||
      biz?.areaName ||
      biz?.locality ||
      biz?.locationDistrict ||
      biz?.locationSector ||
      biz?.locationArea ||
      biz?.coverageArea,
    ""
  );
  if (directZone) return extractZoneFromLocation(directZone) || directZone;

  const raw = text(
    biz?.locationLabel ||
      biz?.address ||
      biz?.fullAddress ||
      biz?.cityName ||
      biz?.locationCity ||
      biz?.city,
    ""
  );

  return extractZoneFromLocation(raw) || getMarket(biz?.countryCode).name;
}

export function getBusinessDescription(biz) {
  const raw = text(biz?.shortDescription || biz?.description || biz?.about || biz?.bio, "");
  if (!raw) return "";
  return raw.length > 96 ? `${raw.slice(0, 93).trim()}...` : raw;
}

export function getRatingInfo(biz) {
  const rating = Number(biz?.rating || biz?.averageRating || biz?.stars || 0);
  const reviews = Number(biz?.reviewsCount || biz?.reviewCount || biz?.ratingsCount || biz?.reviews || 0);
  return {
    label: rating ? rating.toFixed(1) : "Nuevo",
    reviews: Number.isFinite(reviews) ? reviews : 0,
    hasRating: Number.isFinite(rating) && rating > 0 && rating <= 5 && reviews > 0,
  };
}

function getVisitBadges(biz) {
  const mode = String(biz?.serviceMode || biz?.mode || biz?.visitMode || "").toLowerCase();
  const hasAreas =
    (Array.isArray(biz?.serviceAreas) && biz.serviceAreas.length > 0) ||
    (Array.isArray(biz?.serviceAreasAdvanced) && biz.serviceAreasAdvanced.length > 0);
  const badges = [];
  if (biz?.featured) badges.push("Destacado");
  if (mode.includes("delivery") || mode.includes("domicilio") || mode.includes("hybrid") || mode.includes("mixto") || hasAreas) badges.push("A domicilio");
  if (!mode || mode.includes("onsite") || mode.includes("local") || mode.includes("hybrid") || mode.includes("mixto")) badges.push("En local");
  if (biz?.online === true || biz?.virtual === true || biz?.offers?.online === true) badges.push("Online");
  if (biz?.availableToday || biz?.isOpen || biz?.openNow || biz?.acceptsToday) badges.push("Disponible hoy");
  return Array.from(new Set(badges)).slice(0, 3);
}

function normalizeChipKey(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function getServiceChips(biz, max = 2) {
  const services = Array.isArray(biz?.services) ? biz.services : [];
  const blocked = new Set([
    "servicio_principal",
    "principal",
    "service_principal",
    "main_service",
    "servicio",
  ]);

  const seen = new Set();
  const out = [];

  for (const service of services) {
    const label = normalizeServiceName(service);
    const key = normalizeChipKey(label);
    if (!label || !key || blocked.has(key)) continue;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(label);
    if (out.length >= max) break;
  }

  return out;
}

function PinIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.15" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 21s7-5.1 7-12a7 7 0 1 0-14 0c0 6.9 7 12 7 12Z" />
      <circle cx="12" cy="9" r="2.4" />
    </svg>
  );
}

function HeartIcon({ filled = false }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8Z" />
    </svg>
  );
}

function LogoFrame({ src, name, variant }) {
  const [failed, setFailed] = React.useState(false);
  const isFeatured = variant === "featured" || variant === "event";
  if (!src || failed) {
    return (
      <div className="brc-logo-fallback">
        <span>{initials(name)}</span>
      </div>
    );
  }

  // Search results use the same RAW logo logic as BusinessPage:
  // original uploaded visual, no SmartLogoFrame clean-preview, no generated canvas,
  // no forced cover crop. The image keeps its natural proportions.
  return (
    <div className={`brc-logo-frame ${isFeatured ? "is-featured" : ""}`}>
      <img
        className="brc-logo-raw-img"
        src={src}
        alt={name}
        loading="lazy"
        decoding="async"
        onError={() => setFailed(true)}
      />
    </div>
  );
}

export default function BusinessResultCard({
  biz = {},
  variant = "list",
  priority = false,
  isFavorite = false,
  onToggleFavorite,
  onOpenBusiness = () => {},
  onOpenBooking = () => {},
  actionLabel = "Reservar",
  contextLabel = "Marketplace",
}) {
  const name = getBusinessName(biz);
  const image = getBusinessImage(biz);
  const location = getBusinessLocation(biz);
  const description = getBusinessDescription(biz);
  const rating = getRatingInfo(biz);
  const badges = getVisitBadges(biz);
  const services = getServiceChips(biz, variant === "featured" ? 1 : 2);
  const matchedService = Array.isArray(biz.__matchedServices) ? biz.__matchedServices[0] : null;
  const servicePrice = matchedService && typeof matchedService.price === 'number' && Number.isFinite(matchedService.price) ? matchedService.price : null;
  const isCompact = variant === "compact";

  const handleOpen = () => onOpenBusiness(biz);
  const handleBooking = (event) => {
    event.stopPropagation();
    onOpenBooking(biz);
  };
  const handleFavorite = (event) => {
    event.stopPropagation();
    if (typeof onToggleFavorite === "function") onToggleFavorite(biz?.id || biz?.businessId, biz);
  };

  return (
    <article
      className={`brc-card brc-${variant} ${priority ? "is-priority" : ""}`}
      onClick={handleOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); handleOpen(); }
      }}
    >
      <style>{styles}</style>

      <div className="brc-media">
        <LogoFrame src={image} name={name} variant={variant} />
        {rating.hasRating && <div className="brc-rating"><span>★</span><strong>{rating.label}</strong><em>({rating.reviews})</em></div>}
        {typeof onToggleFavorite === "function" ? (
          <button type="button" className={`brc-fav ${isFavorite ? "is-active" : ""}`} onClick={handleFavorite} aria-label={isFavorite ? "Quitar favorito" : "Guardar favorito"}>
            <HeartIcon filled={isFavorite} />
          </button>
        ) : null}
      </div>

      <div className="brc-body">
        <div className="brc-topline">
          <span>{contextLabel}</span>

        </div>
        <div className="brc-copy">
          <h3>{name}</h3>
          <div className="brc-location"><PinIcon /><span>{location}</span></div>
          {!isCompact && description ? <p>{description}</p> : null}
        </div>

        <div className="brc-meta-row">
          {badges.map((badge) => <span className="brc-badge" key={badge}>{badge}</span>)}
          {services.slice(0, Math.max(0, 3 - badges.length)).map((service) => <span className="brc-service" key={service}>{service}</span>)}
        </div>

        {matchedService && <p className="brc-match">{normalizeServiceName(matchedService)}{servicePrice !== null && servicePrice >= 0 ? ' · Desde ' + formatMarketMoney(servicePrice,matchedService.currency || biz.currency || getMarket(biz.countryCode).currency,biz.locale || getMarket(biz.countryCode).locale) : ''}</p>}
        <div className="brc-actions">
          <button type="button" className="brc-view" onClick={(e) => { e.stopPropagation(); handleOpen(); }}>Ver negocio</button>
          <button type="button" className="brc-book brc-reserve-pill" aria-label={actionLabel} onClick={handleBooking}>{actionLabel}</button>
        </div>
      </div>
    </article>
  );
}

const styles = `
  .brc-card {
    width: 100%;
    min-width: 0;
    border: 1px solid rgba(226,232,240,.86);
    background: rgba(255,255,255,.99);
    border-radius: 28px;
    padding: 14px;
    display: grid;
    grid-template-columns: 240px minmax(0, 1fr);
    gap: 20px;
    cursor: pointer;
    box-shadow: 0 14px 38px rgba(15,23,42,.052);
    transition: transform 170ms ease, box-shadow 170ms ease, border-color 170ms ease, background 170ms ease;
    box-sizing: border-box;
  }

  .brc-card:hover {
    transform: translateY(-1px);
    border-color: rgba(0,103,95,.18);
    box-shadow: 0 22px 54px rgba(15,23,42,.078);
    background: #fff;
  }

  .brc-card:active { transform: scale(.994); }

  .brc-media {
    position: relative;
    width: 100%;
    min-height: 136px;
    border-radius: 24px;
    overflow: visible;
    background: transparent;
    border: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    isolation: isolate;
  }

  .brc-logo-frame {
    position: relative;
    width: 100%;
    height: auto;
    min-height: 136px;
    overflow: visible;
    border-radius: inherit;
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .brc-logo-raw-img {
    display: block;
    width: auto;
    height: auto;
    max-width: 100%;
    max-height: 136px;
    object-fit: contain;
    object-position: center center;
    border: 0;
    border-radius: 24px;
    background: transparent;
    box-shadow: none;
    margin: 0 auto;
    transform: none;
  }

  .brc-logo-frame::after {
    display: none;
    content: none;
  }

  .brc-logo-fallback {
    width: 100%;
    height: 100%;
    display: grid;
    place-items: center;
    background: radial-gradient(circle at top left, rgba(0,103,95,.10), transparent 34%), linear-gradient(135deg, #ffffff, #f8fafc);
  }

  .brc-logo-fallback span {
    width: 58px;
    height: 58px;
    border-radius: 20px;
    background: #fff;
    color: ${BRAND};
    display: grid;
    place-items: center;
    font-size: 19px;
    font-weight: 950;
    box-shadow: 0 14px 30px rgba(15,23,42,.09);
  }

  .brc-rating {
    position: absolute;
    left: 10px;
    top: 10px;
    min-height: 28px;
    border-radius: 999px;
    padding: 0 9px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: rgba(15,23,42,.62);
    color: #fff;
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    font-size: 11px;
    font-weight: 950;
    z-index: 4;
  }

  .brc-rating span { color: #fbbf24; }
  .brc-rating em { font-style: normal; opacity: .78; font-size: 10px; }

  .brc-fav {
    position: absolute;
    right: 10px;
    top: 10px;
    width: 32px;
    height: 32px;
    border-radius: 999px;
    border: 1px solid rgba(255,255,255,.50);
    background: rgba(15,23,42,.42);
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    z-index: 4;
  }

  .brc-fav.is-active { color: #fbbf24; }

  .brc-body {
    min-width: 0;
    display: grid;
    grid-template-rows: auto auto auto 1fr;
    align-content: start;
    gap: 9px;
    padding: 2px 0;
  }

  .brc-topline {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    min-width: 0;
  }

  .brc-topline span {
    color: #00675f;
    font-size: 10px;
    line-height: 1;
    font-weight: 950;
    text-transform: uppercase;
    letter-spacing: .12em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .brc-topline em {
    color: #94a3b8;
    font-size: 10.5px;
    font-weight: 850;
    font-style: normal;
    white-space: nowrap;
  }

  .brc-copy { min-width: 0; }

  .brc-copy h3 {
    margin: 0;
    color: #0b1428;
    font-size: 21px;
    line-height: 1.03;
    letter-spacing: -.045em;
    font-weight: 950;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .brc-location {
    margin-top: 8px;
    color: #64748b;
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    font-size: 12.8px;
    font-weight: 780;
  }

  .brc-location span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .brc-copy p {
    margin: 8px 0 0;
    color: #64748b;
    font-size: 13px;
    line-height: 1.38;
    font-weight: 700;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .brc-meta-row {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    min-height: 24px;
    align-items: center;
  }

  .brc-badge, .brc-service {
    border: 1px solid rgba(226,232,240,.84);
    background: #f8fafc;
    color: #475569;
    min-height: 24px;
    border-radius: 999px;
    padding: 0 8px;
    display: inline-flex;
    align-items: center;
    font-size: 10.8px;
    line-height: 1;
    font-weight: 850;
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .brc-badge {
    border-color: rgba(0,103,95,.14);
    background: #ecfdf5;
    color: #047857;
  }

  .brc-actions {
    margin-top: auto;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 9px;
  }

  .brc-actions button {
    min-height: 38px;
    border-radius: 999px;
    padding: 0 15px;
    font-size: 12.2px;
    font-weight: 950;
    cursor: pointer;
  }

  .brc-view {
    border: 1px solid rgba(226,232,240,.95);
    background: #fff;
    color: #334155;
  }

  .brc-book {
    border: 0;
    background: #00675f;
    color: #fff;
    box-shadow: 0 11px 24px rgba(0,103,95,.18);
  }

  .brc-featured {
    grid-template-columns: 250px minmax(0, 1fr);
    border-radius: 28px;
  }

  .brc-featured .brc-media { min-height: 141px; }

  .brc-event {
    grid-template-columns: 1fr;
    padding: 12px;
  }

  .brc-event .brc-media { min-height: 178px; }
  .brc-event .brc-copy h3 { white-space: normal; }

  .brc-compact {
    grid-template-columns: 140px minmax(0, 1fr);
    padding: 11px;
    border-radius: 22px;
  }

  .brc-compact .brc-media {
    min-height: 79px;
    border-radius: 18px;
  }

  .brc-compact .brc-logo-frame {
    min-height: 79px;
  }

  .brc-compact .brc-logo-raw-img {
    max-height: 79px;
    border-radius: 18px;
  }

  .brc-compact .brc-actions,
  .brc-compact .brc-meta-row {
    display: none;
  }

  @media (max-width: 900px) {
    .brc-card {
      grid-template-columns: 190px minmax(0, 1fr);
    }

    .brc-media {
      min-height: 107px;
    }

    .brc-logo-frame {
      min-height: 107px;
    }

    .brc-logo-raw-img {
      max-height: 107px;
    }

    .brc-featured {
      grid-template-columns: 200px minmax(0, 1fr);
    }
  }

  @media (max-width: 720px) {
    .brc-card {
      grid-template-columns: 124px minmax(0, 1fr);
      gap: 12px;
      padding: 11px;
      border-radius: 24px;
    }

    .brc-media {
      min-height: 70px;
      border-radius: 18px;
    }

    .brc-logo-frame {
      min-height: 70px;
    }

    .brc-logo-raw-img {
      max-height: 70px;
      border-radius: 18px;
    }

    .brc-copy h3 {
      font-size: 17px;
      letter-spacing: -.035em;
    }

    .brc-copy p {
      display: none;
    }

    .brc-topline em {
      display: none;
    }

    .brc-meta-row {
      gap: 5px;
    }

    .brc-badge, .brc-service {
      min-height: 22px;
      padding: 0 7px;
      font-size: 10px;
      max-width: 112px;
    }

    .brc-actions {
      justify-content: stretch;
    }

    .brc-actions button {
      flex: 1;
      min-height: 34px;
      padding: 0 9px;
      font-size: 11px;
    }

    .brc-event {
      grid-template-columns: 1fr;
    }

    .brc-event .brc-media {
      height: 150px;
    }
  }

  @media (max-width: 420px) {
    .brc-card {
      grid-template-columns: 112px minmax(0, 1fr);
    }

    .brc-media {
      min-height: 63px;
    }

    .brc-logo-frame {
      min-height: 63px;
    }

    .brc-logo-raw-img {
      max-height: 63px;
    }

    .brc-view {
      display: none;
    }

    .brc-actions {
      justify-content: flex-end;
    }

    .brc-actions button {
      flex: 0 0 auto;
    }
  }

  /* =========================================================
     LOOP BUSINESS RESULT CARD — COMPACT PREMIUM MOBILE V54
     The card owns its own layout, no DOM tagging needed.
     - Smaller top-right reserve pill.
     - No overlap with name/location.
     - Hides "Ver negocio" on mobile.
     - Service chips are smaller and inline.
     ========================================================= */
  @media (max-width: 720px) {
    .brc-card {
      position: relative !important;
      grid-template-columns: 106px minmax(0, 1fr) !important;
      gap: 10px !important;
      min-height: 104px !important;
      padding: 10px !important;
      border-radius: 23px !important;
      overflow: hidden !important;
      border: 1px solid rgba(204,251,241,.76) !important;
      background:
        radial-gradient(circle at 96% 0%, rgba(0,137,127,.055), transparent 34%),
        rgba(255,255,255,.96) !important;
      box-shadow: 0 12px 30px rgba(54,41,25,.06) !important;
    }

    .brc-media {
      width: 106px !important;
      min-height: 82px !important;
      height: 82px !important;
      border-radius: 18px !important;
      align-self: center !important;
      background: #f8fafc !important;
      overflow: hidden !important;
    }

    .brc-logo-frame {
      min-height: 82px !important;
      height: 82px !important;
      border-radius: 18px !important;
      background: #f8fafc !important;
      overflow: hidden !important;
    }

    .brc-logo-raw-img {
      max-height: 74px !important;
      max-width: 92px !important;
      border-radius: 16px !important;
    }

    .brc-rating {
      left: 8px !important;
      top: 8px !important;
      min-height: 25px !important;
      height: 25px !important;
      padding: 0 8px !important;
      font-size: 10px !important;
      gap: 3px !important;
      background: rgba(15,23,42,.60) !important;
    }

    .brc-fav {
      right: 8px !important;
      top: 8px !important;
      width: 29px !important;
      height: 29px !important;
    }

    .brc-body {
      min-width: 0 !important;
      display: grid !important;
      grid-template-rows: auto auto auto !important;
      align-content: start !important;
      gap: 5px !important;
      padding: 0 72px 0 0 !important;
    }

    .brc-topline {
      min-width: 0 !important;
      height: 10px !important;
      min-height: 10px !important;
      align-items: center !important;
      overflow: hidden !important;
    }

    .brc-topline span {
      max-width: 100% !important;
      font-size: 9.3px !important;
      line-height: 1 !important;
      letter-spacing: .145em !important;
      color: #00675f !important;
    }

    .brc-topline em {
      display: none !important;
    }

    .brc-copy {
      min-width: 0 !important;
    }

    .brc-copy h3 {
      max-width: 100% !important;
      margin: 0 !important;
      color: #0f172a !important;
      font-size: 14.4px !important;
      line-height: 1.06 !important;
      letter-spacing: -.042em !important;
      font-weight: 950 !important;
      white-space: nowrap !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
    }

    .brc-location {
      max-width: 100% !important;
      margin-top: 5px !important;
      gap: 4px !important;
      color: #667085 !important;
      font-size: 10.6px !important;
      line-height: 1.05 !important;
      font-weight: 800 !important;
      overflow: hidden !important;
      white-space: nowrap !important;
    }

    .brc-location svg {
      width: 11px !important;
      height: 11px !important;
      flex: 0 0 auto !important;
    }

    .brc-location span {
      min-width: 0 !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
      white-space: nowrap !important;
    }

    .brc-copy p {
      display: none !important;
    }

    .brc-meta-row {
      min-height: 18px !important;
      max-height: 39px !important;
      display: flex !important;
      flex-wrap: wrap !important;
      align-items: center !important;
      gap: 4px !important;
      overflow: hidden !important;
      margin-top: 1px !important;
    }

    .brc-badge,
    .brc-service {
      width: auto !important;
      min-width: 0 !important;
      max-width: 86px !important;
      height: 18px !important;
      min-height: 18px !important;
      padding: 0 6px !important;
      border-radius: 999px !important;
      font-size: 8.8px !important;
      line-height: 1 !important;
      font-weight: 950 !important;
      letter-spacing: -.005em !important;
      box-shadow: none !important;
    }

    .brc-badge {
      border-color: rgba(0,103,95,.14) !important;
      background: #ecfdf5 !important;
      color: #047857 !important;
    }

    .brc-service {
      border-color: rgba(226,232,240,.90) !important;
      background: #f8fafc !important;
      color: #475569 !important;
    }

    .brc-actions {
      position: absolute !important;
      top: 8px !important;
      right: 8px !important;
      z-index: 24 !important;
      display: block !important;
      margin: 0 !important;
      padding: 0 !important;
      width: auto !important;
      height: auto !important;
    }

    .brc-actions .brc-view {
      display: none !important;
    }

    .brc-card .brc-actions .brc-book,
    .brc-card .brc-actions .brc-reserve-pill {
      width: auto !important;
      min-width: 62px !important;
      max-width: 72px !important;
      height: 28px !important;
      min-height: 28px !important;
      max-height: 28px !important;
      padding: 0 9px !important;
      border: 0 !important;
      border-radius: 999px !important;
      background: linear-gradient(135deg, #00796f 0%, #009080 100%) !important;
      color: #ffffff !important;
      box-shadow: 0 9px 18px rgba(0,103,95,.19) !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      flex: 0 0 auto !important;
      font-size: 10px !important;
      line-height: 1 !important;
      font-weight: 950 !important;
      letter-spacing: -.01em !important;
      white-space: nowrap !important;
    }
  }

  @media (max-width: 390px) {
    .brc-card {
      grid-template-columns: 98px minmax(0, 1fr) !important;
      gap: 9px !important;
      min-height: 100px !important;
      padding: 9px !important;
    }

    .brc-media,
    .brc-logo-frame {
      width: 98px !important;
      min-height: 78px !important;
      height: 78px !important;
    }

    .brc-logo-raw-img {
      max-width: 86px !important;
      max-height: 70px !important;
    }

    .brc-body {
      padding-right: 68px !important;
      gap: 4px !important;
    }

    .brc-copy h3 {
      font-size: 13.8px !important;
    }

    .brc-location {
      font-size: 10.2px !important;
    }

    .brc-badge,
    .brc-service {
      max-width: 78px !important;
      height: 17px !important;
      min-height: 17px !important;
      padding: 0 5px !important;
      font-size: 8.4px !important;
    }

    .brc-card .brc-actions .brc-book,
    .brc-card .brc-actions .brc-reserve-pill {
      min-width: 58px !important;
      max-width: 66px !important;
      height: 27px !important;
      min-height: 27px !important;
      max-height: 27px !important;
      padding: 0 8px !important;
      font-size: 9.6px !important;
    }
  }



  /* =========================================================
     LOOP BUSINESS RESULT CARD — FINAL MOBILE CONTRACT V56
     Fix verified by runtime audit:
     - Previous card had body padding-right: 92px and title width around 13px.
     - "Resultado" consumed the first text row.
     - ServiceResultsPage V53 tagging could turn brc-body/meta into chip wrappers.
     Contract:
     [thumbnail] [name/location/chips + small reserve CTA]
     No absolute CTA over the text. No "Resultado" on mobile.
     ========================================================= */
  @media (max-width: 720px) {
    .brc-card.brc-list,
    .sr-pro-page .sr-main-list .brc-card.brc-list,
    .sr-pro-page .sr-main-list .brc-card.brc-list.sr-result-card-v53 {
      position: relative !important;
      grid-template-columns: 112px minmax(0, 1fr) !important;
      gap: 10px !important;
      min-height: 104px !important;
      padding: 10px !important;
      border-radius: 23px !important;
      overflow: hidden !important;
      align-items: center !important;
    }

    .brc-card.brc-list .brc-media,
    .sr-pro-page .sr-main-list .brc-card.brc-list .brc-media {
      width: 112px !important;
      min-width: 112px !important;
      max-width: 112px !important;
      height: 82px !important;
      min-height: 82px !important;
      max-height: 82px !important;
      border-radius: 18px !important;
      overflow: hidden !important;
      background: #f6f8fb !important;
      align-self: center !important;
      display: block !important;
      box-shadow: inset 0 0 0 1px rgba(226,232,240,.72) !important;
    }

    .brc-card.brc-list .brc-logo-frame,
    .sr-pro-page .sr-main-list .brc-card.brc-list .brc-logo-frame {
      width: 100% !important;
      height: 100% !important;
      min-height: 100% !important;
      max-height: 100% !important;
      border-radius: inherit !important;
      overflow: hidden !important;
      background: #f6f8fb !important;
      display: block !important;
    }

    .brc-card.brc-list .brc-logo-raw-img,
    .sr-pro-page .sr-main-list .brc-card.brc-list .brc-logo-raw-img {
      width: 100% !important;
      height: 100% !important;
      max-width: none !important;
      max-height: none !important;
      object-fit: cover !important;
      object-position: center center !important;
      border-radius: inherit !important;
      display: block !important;
      margin: 0 !important;
      transform: none !important;
    }

    .brc-card.brc-list .brc-rating,
    .sr-pro-page .sr-main-list .brc-card.brc-list .brc-rating {
      left: 7px !important;
      top: 7px !important;
      min-height: 24px !important;
      height: 24px !important;
      padding: 0 8px !important;
      font-size: 9.6px !important;
      gap: 3px !important;
    }

    .brc-card.brc-list .brc-topline,
    .sr-pro-page .sr-main-list .brc-card.brc-list .brc-topline {
      display: none !important;
    }

    .brc-card.brc-list .brc-body,
    .brc-card.brc-list .brc-body.sr-result-chip-wrap-v53,
    .sr-pro-page .sr-main-list .brc-card.brc-list .brc-body,
    .sr-pro-page .sr-main-list .brc-card.brc-list .brc-body.sr-result-chip-wrap-v53,
    .sr-pro-page .sr-main-list .brc-card.brc-list:has(.brc-reserve-pill) .brc-body {
      min-width: 0 !important;
      width: 100% !important;
      max-width: none !important;
      display: grid !important;
      grid-template-columns: minmax(0, 1fr) auto !important;
      grid-template-rows: auto auto !important;
      align-items: start !important;
      align-content: center !important;
      gap: 5px 8px !important;
      padding: 0 !important;
      margin: 0 !important;
      overflow: visible !important;
      box-sizing: border-box !important;
    }

    .brc-card.brc-list .brc-copy,
    .sr-pro-page .sr-main-list .brc-card.brc-list .brc-copy {
      grid-column: 1 / 2 !important;
      grid-row: 1 / 2 !important;
      min-width: 0 !important;
      width: 100% !important;
      max-width: 100% !important;
      padding: 0 !important;
      margin: 0 !important;
    }

    .brc-card.brc-list .brc-copy h3,
    .sr-pro-page .sr-main-list .brc-card.brc-list .brc-copy h3 {
      width: 100% !important;
      max-width: 100% !important;
      margin: 0 !important;
      padding: 0 !important;
      color: #0f172a !important;
      font-size: 14.4px !important;
      line-height: 1.05 !important;
      letter-spacing: -.042em !important;
      font-weight: 950 !important;
      white-space: nowrap !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
    }

    .brc-card.brc-list .brc-location,
    .sr-pro-page .sr-main-list .brc-card.brc-list .brc-location {
      width: 100% !important;
      max-width: 100% !important;
      margin: 5px 0 0 !important;
      padding: 0 !important;
      gap: 4px !important;
      color: #667085 !important;
      font-size: 10.6px !important;
      line-height: 1.05 !important;
      font-weight: 800 !important;
      overflow: hidden !important;
      white-space: nowrap !important;
    }

    .brc-card.brc-list .brc-location svg,
    .sr-pro-page .sr-main-list .brc-card.brc-list .brc-location svg {
      width: 11px !important;
      height: 11px !important;
      flex: 0 0 auto !important;
    }

    .brc-card.brc-list .brc-location span,
    .sr-pro-page .sr-main-list .brc-card.brc-list .brc-location span {
      min-width: 0 !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
      white-space: nowrap !important;
    }

    .brc-card.brc-list .brc-actions,
    .brc-card.brc-list .brc-actions.sr-result-chip-wrap-v53,
    .sr-pro-page .sr-main-list .brc-card.brc-list .brc-actions {
      grid-column: 2 / 3 !important;
      grid-row: 1 / 2 !important;
      position: static !important;
      top: auto !important;
      right: auto !important;
      display: flex !important;
      align-items: flex-start !important;
      justify-content: flex-end !important;
      width: auto !important;
      height: auto !important;
      margin: 0 !important;
      padding: 0 !important;
      z-index: 3 !important;
    }

    .brc-card.brc-list .brc-actions .brc-view,
    .sr-pro-page .sr-main-list .brc-card.brc-list .brc-actions .brc-view {
      display: none !important;
    }

    .brc-card.brc-list .brc-actions .brc-book,
    .brc-card.brc-list .brc-actions .brc-book.sr-result-reserve-cta-v53,
    .brc-card.brc-list .brc-actions .brc-reserve-pill,
    .sr-pro-page .sr-main-list .brc-card.brc-list .brc-actions .brc-book,
    .sr-pro-page .sr-main-list .brc-card.brc-list .brc-actions .brc-book.sr-result-reserve-cta-v53,
    .sr-pro-page .sr-main-list .brc-card.brc-list .brc-actions .brc-reserve-pill {
      position: static !important;
      top: auto !important;
      right: auto !important;
      width: auto !important;
      min-width: 56px !important;
      max-width: 64px !important;
      height: 27px !important;
      min-height: 27px !important;
      max-height: 27px !important;
      padding: 0 8px !important;
      border: 0 !important;
      border-radius: 999px !important;
      background: linear-gradient(135deg, #00796f 0%, #009080 100%) !important;
      color: #fff !important;
      box-shadow: 0 8px 18px rgba(0,103,95,.17) !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      flex: 0 0 auto !important;
      font-size: 9.4px !important;
      line-height: 1 !important;
      font-weight: 950 !important;
      letter-spacing: -.01em !important;
      white-space: nowrap !important;
      transform: none !important;
    }

    .brc-card.brc-list .brc-meta-row,
    .brc-card.brc-list .brc-meta-row.sr-result-chip-v53,
    .brc-card.brc-list .brc-meta-row.sr-result-chip-wrap-v53,
    .sr-pro-page .sr-main-list .brc-card.brc-list .brc-meta-row,
    .sr-pro-page .sr-main-list .brc-card.brc-list .brc-meta-row.sr-result-chip-v53,
    .sr-pro-page .sr-main-list .brc-card.brc-list .brc-meta-row.sr-result-chip-wrap-v53 {
      grid-column: 1 / -1 !important;
      grid-row: 2 / 3 !important;
      width: 100% !important;
      min-width: 0 !important;
      max-width: 100% !important;
      min-height: 18px !important;
      height: auto !important;
      max-height: 38px !important;
      display: flex !important;
      flex-direction: row !important;
      flex-wrap: wrap !important;
      align-items: center !important;
      justify-content: flex-start !important;
      gap: 4px !important;
      margin: 0 !important;
      padding: 0 !important;
      border: 0 !important;
      background: transparent !important;
      box-shadow: none !important;
      overflow: hidden !important;
    }

    .brc-card.brc-list .brc-badge,
    .brc-card.brc-list .brc-service,
    .brc-card.brc-list .brc-badge.sr-result-chip-v53,
    .brc-card.brc-list .brc-service.sr-result-chip-v53,
    .sr-pro-page .sr-main-list .brc-card.brc-list .brc-badge,
    .sr-pro-page .sr-main-list .brc-card.brc-list .brc-service {
      width: auto !important;
      min-width: 0 !important;
      max-width: 86px !important;
      height: 18px !important;
      min-height: 18px !important;
      max-height: 18px !important;
      padding: 0 6px !important;
      border-radius: 999px !important;
      font-size: 8.8px !important;
      line-height: 1 !important;
      font-weight: 950 !important;
      letter-spacing: -.005em !important;
      box-shadow: none !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      white-space: nowrap !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
    }
  }

  @media (max-width: 390px) {
    .brc-card.brc-list,
    .sr-pro-page .sr-main-list .brc-card.brc-list,
    .sr-pro-page .sr-main-list .brc-card.brc-list.sr-result-card-v53 {
      grid-template-columns: 104px minmax(0, 1fr) !important;
      gap: 9px !important;
      min-height: 100px !important;
      padding: 9px !important;
    }

    .brc-card.brc-list .brc-media,
    .sr-pro-page .sr-main-list .brc-card.brc-list .brc-media {
      width: 104px !important;
      min-width: 104px !important;
      max-width: 104px !important;
      height: 78px !important;
      min-height: 78px !important;
      max-height: 78px !important;
    }

    .brc-card.brc-list .brc-copy h3,
    .sr-pro-page .sr-main-list .brc-card.brc-list .brc-copy h3 {
      font-size: 13.8px !important;
    }

    .brc-card.brc-list .brc-actions .brc-book,
    .brc-card.brc-list .brc-actions .brc-book.sr-result-reserve-cta-v53,
    .brc-card.brc-list .brc-actions .brc-reserve-pill,
    .sr-pro-page .sr-main-list .brc-card.brc-list .brc-actions .brc-book,
    .sr-pro-page .sr-main-list .brc-card.brc-list .brc-actions .brc-book.sr-result-reserve-cta-v53,
    .sr-pro-page .sr-main-list .brc-card.brc-list .brc-actions .brc-reserve-pill {
      min-width: 54px !important;
      max-width: 60px !important;
      height: 26px !important;
      min-height: 26px !important;
      max-height: 26px !important;
      padding: 0 7px !important;
      font-size: 9px !important;
    }
  }


`;
